import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RestaurantDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mealSource, setMealSource] = useState('');
  const [formData, setFormData] = useState({
    mealName: '',
    cuisineType: '',
    quantity: '',
    mealPreparedAt: '',
    mealFreshTill: '',
    pickupPoint: '',
    pickupTime: '',
    transporterNotes: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [allMeals, setAllMeals] = useState([]); // Unified meal list
  const [editingMeal, setEditingMeal] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showDeleteProfile, setShowDeleteProfile] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ password: '', confirmation: '' });
  const [otpInputs, setOtpInputs] = useState({}); // OTP inputs for each listing
  const [verifyingOtp, setVerifyingOtp] = useState(null); // Track which listing is being verified

  useEffect(() => {
    checkAuthentication();
    loadAllMeals();
  }, []);

  const checkAuthentication = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.role !== 'restaurant') {
        toast.error('Access denied. Restaurant role required.');
        navigate('/dashboard');
        return;
      }
      
      setUser(response.data);
    } catch (error) {
      toast.error('Please login to access restaurant dashboard');
      navigate('/auth');
    } finally {
      setLoading(false);
    }
  };

  const loadAllMeals = async () => {
    try {
      // Load workflow listings (main source of truth)
      const response = await axios.get(`${API}/workflow/listings/my`, {
        withCredentials: true
      });
      setAllMeals(response.data);
    } catch (error) {
      console.log('Failed to load meals:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!mealSource) {
      errors.mealSource = 'Please select meal source (Restaurant or Bakery)';
    }
    
    if (!formData.mealName.trim()) {
      errors.mealName = 'Meal name is required';
    }
    
    if (!formData.cuisineType.trim()) {
      errors.cuisineType = 'Cuisine type is required';
    }
    
    if (!formData.quantity || parseInt(formData.quantity) <= 0) {
      errors.quantity = 'Valid quantity is required';
    }
    
    // Either mealPreparedAt or mealFreshTill must be provided
    if (!formData.mealPreparedAt && !formData.mealFreshTill) {
      errors.mealTiming = 'Either "Meal Prepared At" or "Meal Fresh Till" must be provided';
    }
    
    if (!formData.pickupPoint.trim()) {
      errors.pickupPoint = 'Pickup point is required';
    }
    
    if (!formData.pickupTime) {
      errors.pickupTime = 'Pickup time is required';
    }

    // Singapore-specific validation
    if (user?.country === 'Singapore' && formData.mealPreparedAt && formData.pickupTime) {
      const preparedAt = new Date(formData.mealPreparedAt);
      const pickupTime = new Date(formData.pickupTime);
      const fourHoursAfterPrep = new Date(preparedAt.getTime() + 4 * 60 * 60 * 1000);
      
      let isValidPickup = false;
      
      // Check if pickup is within 4 hours of preparation
      if (pickupTime <= fourHoursAfterPrep) {
        isValidPickup = true;
      }
      
      // Or check if pickup is at least 1 hour before freshness expiry
      if (formData.mealFreshTill) {
        const freshTill = new Date(formData.mealFreshTill);
        const oneHourBeforeFresh = new Date(freshTill.getTime() - 60 * 60 * 1000);
        
        if (pickupTime <= oneHourBeforeFresh) {
          isValidPickup = true;
        }
      }
      
      if (!isValidPickup) {
        errors.pickupTime = 'Singapore regulation: Pickup must be within 4 hours of preparation OR at least 1 hour before freshness expiry';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getFreshnessCompliance = () => {
    if (!formData.pickupTime || (!formData.mealPreparedAt && !formData.mealFreshTill)) {
      return null;
    }

    if (user?.country === 'Singapore') {
      const pickupTime = new Date(formData.pickupTime);
      let isCompliant = false;

      if (formData.mealPreparedAt) {
        const preparedAt = new Date(formData.mealPreparedAt);
        const fourHoursAfterPrep = new Date(preparedAt.getTime() + 4 * 60 * 60 * 1000);
        if (pickupTime <= fourHoursAfterPrep) {
          isCompliant = true;
        }
      }

      if (formData.mealFreshTill) {
        const freshTill = new Date(formData.mealFreshTill);
        const oneHourBeforeFresh = new Date(freshTill.getTime() - 60 * 60 * 1000);
        if (pickupTime <= oneHourBeforeFresh) {
          isCompliant = true;
        }
      }

      return isCompliant;
    }

    return true; // For India, assume compliance if times are provided
  };

  const handleSaveDraft = async () => {
    if (!mealSource) {
      toast.error('Please select meal source first');
      return;
    }

    try {
      const draftData = {
        ...formData,
        mealSource,
        status: 'draft'
      };
      
      await axios.post(`${API}/meals/draft`, draftData, {
        withCredentials: true
      });
      
      toast.success('Draft saved successfully');
    } catch (error) {
      toast.error('Failed to save draft');
      console.error('Draft save error:', error);
    }
  };

  const handlePublishMeal = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before publishing');
      return;
    }

    try {
      const listingData = {
        meal_name: formData.mealName,
        portions: parseInt(formData.quantity),
        prepared_at: new Date(formData.mealPreparedAt).toISOString(),
        fresh_till: new Date(formData.mealFreshTill).toISOString(),
        pickup_point: formData.pickupPoint,
        city: user?.city || '',
        country: user?.country || '',
        notes: `${mealSource} - ${formData.cuisineType}${formData.transporterNotes ? ' - ' + formData.transporterNotes : ''}`
      };
      
      await axios.post(`${API}/workflow/listings`, listingData, {
        withCredentials: true
      });
      
      toast.success('Meal published successfully! Volunteers can now claim your listing.');
      
      // Reset form
      setFormData({
        mealName: '',
        cuisineType: '',
        quantity: '',
        mealPreparedAt: '',
        mealFreshTill: '',
        pickupPoint: '',
        pickupTime: '',
        transporterNotes: ''
      });
      setMealSource('');
      setValidationErrors({});
      
      // Reload meals
      loadAllMeals();
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to publish meal';
      toast.error(errorMsg);
      console.error('Meal publish error:', error);
    }
  };

  const handleEditMeal = (meal) => {
    // Convert ISO strings back to datetime-local format for form inputs
    const formatForInput = (isoString) => {
      if (!isoString) return '';
      try {
        return new Date(isoString).toISOString().slice(0, 16);
      } catch {
        return '';
      }
    };

    setEditingMeal(meal);
    setMealSource(meal.mealSource);
    setFormData({
      mealName: meal.mealName,
      cuisineType: meal.cuisineType,
      quantity: meal.quantity.toString(),
      mealPreparedAt: formatForInput(meal.mealPreparedAt),
      mealFreshTill: formatForInput(meal.mealFreshTill),
      pickupPoint: meal.pickupPoint,
      pickupTime: formatForInput(meal.pickupTime),
      transporterNotes: meal.transporterNotes || ''
    });
    setValidationErrors({});
  };

  const handleUpdateMeal = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before updating');
      return;
    }

    try {
      const mealData = {
        ...formData,
        mealSource,
        quantity: parseInt(formData.quantity),
        status: editingMeal.status // Keep original status or could make it editable
      };
      
      await axios.put(`${API}/meals/${editingMeal.id}`, mealData, {
        withCredentials: true
      });
      
      toast.success('Meal updated successfully!');
      
      // Reset form and editing state
      setEditingMeal(null);
      setFormData({
        mealName: '',
        cuisineType: '',
        quantity: '',
        mealPreparedAt: '',
        mealFreshTill: '',
        pickupPoint: '',
        pickupTime: '',
        transporterNotes: ''
      });
      setMealSource('');
      setValidationErrors({});
      
      // Reload meals
      loadAllMeals();
      
    } catch (error) {
      toast.error('Failed to update meal');
      console.error('Meal update error:', error);
    }
  };

  const handleDeleteMeal = async (mealId) => {
    try {
      await axios.delete(`${API}/meals/${mealId}`, {
        withCredentials: true
      });
      
      toast.success('Meal deleted successfully!');
      setShowDeleteConfirm(null);
      
      // Reload meals
      loadAllMeals();
      
    } catch (error) {
      toast.error('Failed to delete meal');
      console.error('Meal delete error:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingMeal(null);
    setFormData({
      mealName: '',
      cuisineType: '',
      quantity: '',
      mealPreparedAt: '',
      mealFreshTill: '',
      pickupPoint: '',
      pickupTime: '',
      transporterNotes: ''
    });
    setMealSource('');
    setValidationErrors({});
  };

  const handleOpenProfile = () => {
    setProfileData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: user?.address || '',
      city: user?.city || '',
      country: user?.country || '',
      profile_picture: user?.profile_picture || ''
    });
    setShowProfile(true);
  };

  const handleUpdateProfile = async () => {
    try {
      // Only send fields that have changed
      const updateData = {};
      Object.keys(profileData).forEach(key => {
        if (profileData[key] !== user[key] && profileData[key] !== '') {
          updateData[key] = profileData[key];
        }
      });

      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        return;
      }

      const response = await axios.put(`${API}/auth/profile`, updateData, {
        withCredentials: true
      });

      setUser(response.data);
      toast.success('Profile updated successfully!');
      setShowProfile(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
      console.error('Profile update error:', error);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    try {
      await axios.put(`${API}/auth/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        withCredentials: true
      });

      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
      console.error('Password change error:', error);
    }
  };

  const handleDeleteProfile = async () => {
    if (deleteConfirmation.confirmation.toUpperCase() !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      await axios.delete(`${API}/auth/profile`, {
        data: deleteConfirmation,
        withCredentials: true
      });

      toast.success('Profile deleted successfully');
      // Redirect to home page
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete profile');
      console.error('Profile delete error:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      
      setUser(null);
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      // Even if logout fails, clear local state
      setUser(null);
      navigate('/');
    }
  };

  const handleOtpInputChange = (listingId, value) => {
    setOtpInputs(prev => ({ ...prev, [listingId]: value }));
  };

  const handleVerifyPickupOtp = async (listingId) => {
    const otp = otpInputs[listingId];
    if (!otp || otp.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }

    setVerifyingOtp(listingId);
    try {
      const response = await axios.post(
        `${API}/workflow/listings/${listingId}/verify-pickup-otp`,
        { otp },
        { withCredentials: true }
      );
      toast.success(response.data.message || 'Pickup verified successfully!');
      setOtpInputs(prev => ({ ...prev, [listingId]: '' }));
      loadAllMeals(); // Refresh listings
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to verify OTP';
      toast.error(errorMsg);
    } finally {
      setVerifyingOtp(null);
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('Profile picture must be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setProfileData(prev => ({
          ...prev,
          profile_picture: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'OPEN': 'bg-green-100 text-green-800',
      'CLAIMED_BY_VOLUNTEER': 'bg-blue-100 text-blue-800',
      'BEING_PICKED': 'bg-yellow-100 text-yellow-800',
      'DELIVERED': 'bg-purple-100 text-purple-800',
      'COMPLETED': 'bg-gray-100 text-gray-800',
      'EXPIRED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading restaurant dashboard...</p>
        </div>
      </div>
    );
  }

  const freshnessCompliance = getFreshnessCompliance();
  const canPublish = !Object.keys(validationErrors).length && mealSource && formData.mealName && formData.pickupTime;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="py-8 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {editingMeal ? (
                <>Editing Meal: <span className="text-blue-600">{editingMeal.mealName}</span></>
              ) : (
                <>Welcome, <span className="text-green-600">{user?.name}</span></>
              )}
            </h1>
            <p className="text-xl text-gray-600 mb-2">
              {editingMeal ? 
                'Update your meal details and save changes.' : 
                'Log your surplus meals and manage pickups easily.'
              }
            </p>
            <div className="flex items-center justify-center space-x-4">
              <Badge variant="secondary" className="capitalize">
                {user?.role} • {user?.city}, {user?.country}
              </Badge>
              {user?.country === 'Singapore' && (
                <Badge className="bg-blue-100 text-blue-800">
                  🇸🇬 Singapore Food Safety Rules Apply
                </Badge>
              )}
            </div>
            
            {/* Profile Actions */}
            <div className="flex justify-center space-x-4 mt-4">
              <Button 
                variant="outline" 
                onClick={handleOpenProfile}
                className="text-gray-600 hover:text-green-600"
              >
                👤 Manage Profile
              </Button>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="text-gray-600 hover:text-red-600"
              >
                🚪 Logout
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Meal Source Selection */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">1. Select Meal Source</CardTitle>
            <CardDescription>Choose whether you are logging from a restaurant or bakery</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card 
                className={`cursor-pointer transition-all duration-300 ${
                  mealSource === 'restaurant' 
                    ? 'ring-2 ring-green-500 bg-green-50' 
                    : 'hover:shadow-lg'
                }`}
                onClick={() => setMealSource('restaurant')}
              >
                <CardHeader className="text-center">
                  <div className="text-4xl mb-4">🍽️</div>
                  <CardTitle>Restaurant</CardTitle>
                  <CardDescription>Prepared meals and cooked food items</CardDescription>
                </CardHeader>
              </Card>

              <Card 
                className={`cursor-pointer transition-all duration-300 ${
                  mealSource === 'bakery' 
                    ? 'ring-2 ring-green-500 bg-green-50' 
                    : 'hover:shadow-lg'
                }`}
                onClick={() => setMealSource('bakery')}
              >
                <CardHeader className="text-center">
                  <div className="text-4xl mb-4">🥐</div>
                  <CardTitle>Bakery</CardTitle>
                  <CardDescription>Baked goods and pastries</CardDescription>
                </CardHeader>
              </Card>
            </div>
            {validationErrors.mealSource && (
              <p className="text-red-500 text-sm mt-2">{validationErrors.mealSource}</p>
            )}
          </CardContent>
        </Card>

        {mealSource && (
          <>
            {/* Meal Information Form */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">2. Meal Information</CardTitle>
                <CardDescription>Provide details about the meal you want to donate</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meal Name *
                    </label>
                    <Input
                      placeholder="e.g., Chicken Biryani, Chocolate Croissants"
                      value={formData.mealName}
                      onChange={(e) => handleInputChange('mealName', e.target.value)}
                      className={validationErrors.mealName ? 'border-red-500' : ''}
                    />
                    {validationErrors.mealName && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.mealName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cuisine Type *
                    </label>
                    <Input
                      placeholder="e.g., Indian, Continental, Bakery Items"
                      value={formData.cuisineType}
                      onChange={(e) => handleInputChange('cuisineType', e.target.value)}
                      className={validationErrors.cuisineType ? 'border-red-500' : ''}
                    />
                    {validationErrors.cuisineType && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.cuisineType}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity / Portions Available *
                  </label>
                  <Input
                    type="number"
                    placeholder="e.g., 50"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    className={validationErrors.quantity ? 'border-red-500' : ''}
                  />
                  {validationErrors.quantity && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.quantity}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meal Prepared At
                      {user?.country === 'Singapore' && (
                        <span className="text-blue-600 ml-1" title="Singapore regulation: Pickup must be within 4 hours">ℹ️</span>
                      )}
                    </label>
                    <Input
                      type="datetime-local"
                      value={formData.mealPreparedAt}
                      onChange={(e) => handleInputChange('mealPreparedAt', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meal Fresh Till
                      {user?.country === 'Singapore' && (
                        <span className="text-blue-600 ml-1" title="Singapore regulation: Pickup must be at least 1 hour before expiry">ℹ️</span>
                      )}
                    </label>
                    <Input
                      type="datetime-local"
                      value={formData.mealFreshTill}
                      onChange={(e) => handleInputChange('mealFreshTill', e.target.value)}
                    />
                  </div>
                </div>

                {validationErrors.mealTiming && (
                  <p className="text-red-500 text-sm">{validationErrors.mealTiming}</p>
                )}

                {user?.country === 'Singapore' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 text-sm">
                      <strong>🇸🇬 Singapore Food Safety Regulation:</strong><br />
                      Pickups must occur within 4 hours of meal preparation OR at least 1 hour before freshness expiry.
                      Either condition must be satisfied.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pickup Details */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">3. Pickup Details</CardTitle>
                <CardDescription>Specify where and when the meal can be collected</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pickup Point *
                  </label>
                  <Input
                    placeholder="e.g., Main Kitchen Entrance, Back Door Loading Bay"
                    value={formData.pickupPoint}
                    onChange={(e) => handleInputChange('pickupPoint', e.target.value)}
                    className={validationErrors.pickupPoint ? 'border-red-500' : ''}
                  />
                  {validationErrors.pickupPoint && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.pickupPoint}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pickup Time *
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData.pickupTime}
                    onChange={(e) => handleInputChange('pickupTime', e.target.value)}
                    className={validationErrors.pickupTime ? 'border-red-500' : ''}
                  />
                  {validationErrors.pickupTime && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.pickupTime}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transporter Notes (Optional)
                  </label>
                  <textarea
                    placeholder="Any special instructions for the transporter..."
                    value={formData.transporterNotes}
                    onChange={(e) => handleInputChange('transporterNotes', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows="3"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Summary Box */}
            {(formData.mealName || formData.pickupTime) && (
              <Card className="mb-8 border-2 border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-2xl text-green-800">4. Summary & Compliance</CardTitle>
                  <CardDescription>Review your meal details before publishing</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div>
                        <strong>Meal Name:</strong> {formData.mealName || '(Not provided)'}
                      </div>
                      <div>
                        <strong>Source:</strong> {mealSource ? mealSource.charAt(0).toUpperCase() + mealSource.slice(1) : '(Not selected)'}
                      </div>
                      <div>
                        <strong>Quantity:</strong> {formData.quantity || '(Not provided)'} portions
                      </div>
                      <div>
                        <strong>Pickup Point:</strong> {formData.pickupPoint || '(Not provided)'}
                      </div>
                      <div>
                        <strong>Pickup Time:</strong> {formData.pickupTime ? new Date(formData.pickupTime).toLocaleString() : '(Not provided)'}
                      </div>
                    </div>
                    
                    <div className="flex flex-col justify-center items-center">
                      <div className="text-center">
                        <div className="text-4xl mb-2">
                          {freshnessCompliance === true ? '✅' : freshnessCompliance === false ? '❌' : '⏳'}
                        </div>
                        <div className="font-semibold">
                          Freshness Compliance
                        </div>
                        <div className="text-sm text-gray-600">
                          {freshnessCompliance === true && 'All requirements met'}
                          {freshnessCompliance === false && 'Time constraints not met'}
                          {freshnessCompliance === null && 'Pending validation'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {freshnessCompliance === false && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-sm">
                        ❌ <strong>Adjust pickup time or meal freshness.</strong> The current timing does not meet food safety requirements.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Footer Section */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                  <div className="space-y-2 md:space-y-0">
                    <div className="flex gap-4">
                      {editingMeal ? (
                        <>
                          <Button 
                            variant="outline" 
                            onClick={handleCancelEdit}
                          >
                            Cancel Edit
                          </Button>
                          <Button 
                            onClick={handleUpdateMeal}
                            disabled={!canPublish || freshnessCompliance === false}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Update Meal
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            variant="outline" 
                            onClick={handleSaveDraft}
                            disabled={!mealSource}
                          >
                            Save Draft
                          </Button>
                          <Button 
                            onClick={handlePublishMeal}
                            disabled={!canPublish || freshnessCompliance === false}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Publish for Pickup
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      Need help?{' '}
                      <button 
                        onClick={() => toast.info('Contact Food Connect Support at support@foodconnect.org')}
                        className="text-green-600 hover:text-green-700 underline"
                      >
                        Contact Food Connect Support
                      </button>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* All Meals - Unified View */}
        {allMeals.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Your Published Meals</CardTitle>
              <CardDescription>Track your meal listings and their pickup status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {allMeals.map((meal) => (
                  <Card key={meal.id} className={`border-2 ${meal.needs_pickup_verification ? 'border-orange-400 bg-orange-50' : ''}`}>
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {meal.meal_name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {meal.portions} portions • {meal.pickup_point}
                          </p>
                          <div className="text-xs text-gray-500 mt-2">
                            <div>Prepared: {new Date(meal.prepared_at).toLocaleString()}</div>
                            <div>Fresh Till: {new Date(meal.fresh_till).toLocaleString()}</div>
                          </div>
                          {meal.notes && (
                            <p className="text-sm text-gray-600 mt-2">
                              <span className="font-medium">Notes:</span> {meal.notes}
                            </p>
                          )}
                        </div>
                        <Badge className={getStatusBadgeColor(meal.listing_status)}>
                          {meal.listing_status.replace(/_/g, ' ')}
                        </Badge>
                      </div>

                      {/* NGO Information */}
                      {meal.ngo && (
                        <div className="mt-3 p-3 bg-green-50 rounded border border-green-200">
                          <p className="text-sm font-medium text-green-900 mb-1">🏢 Claimed by NGO:</p>
                          <div className="text-sm text-gray-700">
                            <span className="font-medium">Name:</span> {meal.ngo.name}
                            <br />
                            <span className="font-medium">Address:</span> {meal.ngo.address}
                            <br />
                            <span className="font-medium">Phone:</span> {meal.ngo.phone}
                          </div>
                        </div>
                      )}

                      {/* Volunteer Information */}
                      {meal.volunteer && (
                        <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                          <p className="text-sm font-medium text-blue-900 mb-1">🚚 Volunteer Assigned:</p>
                          <div className="text-sm text-gray-700">
                            <span className="font-medium">Name:</span> {meal.volunteer.name}
                            <br />
                            <span className="font-medium">Phone:</span> {meal.volunteer.phone}
                            <br />
                            <span className="font-medium">Email:</span> {meal.volunteer.email}
                            {meal.assignment_status && (
                              <>
                                <br />
                                <span className="font-medium">Status:</span> {meal.assignment_status}
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* OTP Verification Section */}
                      {meal.needs_pickup_verification && (
                        <div className="mt-4 p-4 bg-orange-100 rounded-lg border-2 border-orange-300">
                          <p className="text-sm font-bold text-orange-900 mb-3">
                            ⚠️ Volunteer is here! Verify pickup OTP:
                          </p>
                          <div className="flex gap-3 items-center">
                            <Input
                              type="text"
                              maxLength={4}
                              placeholder="Enter 4-digit OTP"
                              value={otpInputs[meal.id] || ''}
                              onChange={(e) => handleOtpInputChange(meal.id, e.target.value.replace(/\D/g, ''))}
                              className="w-32 text-center text-lg font-mono"
                            />
                            <Button
                              onClick={() => handleVerifyPickupOtp(meal.id)}
                              disabled={verifyingOtp === meal.id}
                              className="bg-orange-600 hover:bg-orange-700"
                            >
                              {verifyingOtp === meal.id ? 'Verifying...' : 'Verify OTP'}
                            </Button>
                          </div>
                          <p className="text-xs text-orange-700 mt-2">
                            Ask the volunteer for the OTP and enter it above to confirm pickup.
                          </p>
                        </div>
                      )}

                      {/* Pickup Verified Badge */}
                      {meal.pickup_verified && !meal.delivery_verified && (
                        <div className="mt-3 p-2 bg-green-100 rounded text-sm text-green-700">
                          ✅ Pickup verified! Volunteer is on the way to deliver.
                        </div>
                      )}

                      {/* Delivery Complete Badge */}
                      {meal.delivery_verified && (
                        <div className="mt-3 p-2 bg-purple-100 rounded text-sm text-purple-700">
                          🎉 Delivery complete! Thank you for reducing food waste.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this meal? This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDeleteMeal(showDeleteConfirm)}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Profile Management Modal */}
        {showProfile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Profile Management</h3>
                <Button variant="outline" onClick={() => setShowProfile(false)}>×</Button>
              </div>

              {/* Profile Picture */}
              <div className="text-center mb-4">
                <div className="relative inline-block">
                  {profileData.profile_picture ? (
                    <img 
                      src={profileData.profile_picture} 
                      alt="Profile" 
                      className="w-20 h-20 rounded-full object-cover border-2 border-green-200"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center border-2 border-green-200">
                      <span className="text-2xl text-green-600">👤</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    title="Click to upload profile picture"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Click to change profile picture</p>
              </div>

              {/* Profile Form */}
              <div className="space-y-4">
                <Input
                  placeholder="Full Name"
                  value={profileData.name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={profileData.email}
                  onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                />
                <Input
                  placeholder="Phone (optional)"
                  value={profileData.phone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                />
                <Input
                  placeholder="Address"
                  value={profileData.address}
                  onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="City"
                    value={profileData.city}
                    onChange={(e) => setProfileData(prev => ({ ...prev, city: e.target.value }))}
                  />
                  <select
                    value={profileData.country}
                    onChange={(e) => setProfileData(prev => ({ ...prev, country: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="India">India</option>
                    <option value="Singapore">Singapore</option>
                  </select>
                </div>
              </div>

              {/* Profile Actions */}
              <div className="flex flex-col gap-2 mt-6">
                <Button onClick={handleUpdateProfile} className="bg-green-600 hover:bg-green-700">
                  Update Profile
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowPasswordChange(true)}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Change Password
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteProfile(true)}
                  className="text-red-600 hover:text-red-700"
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordChange && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Change Password</h3>
                <Button variant="outline" onClick={() => setShowPasswordChange(false)}>×</Button>
              </div>
              
              <div className="space-y-4">
                <Input
                  type="password"
                  placeholder="Current Password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                />
                <Input
                  type="password"
                  placeholder="New Password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                />
                <Input
                  type="password"
                  placeholder="Confirm New Password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                />
              </div>

              <div className="flex gap-2 mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setShowPasswordChange(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleChangePassword}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Change Password
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Profile Modal */}
        {showDeleteProfile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-red-600">⚠️ Delete Account</h3>
                <Button variant="outline" onClick={() => setShowDeleteProfile(false)}>×</Button>
              </div>
              
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  This will permanently delete your account and all associated data including:
                </p>
                <ul className="text-sm text-gray-500 list-disc list-inside space-y-1">
                  <li>All meal logs and history</li>
                  <li>Published surplus food entries</li>
                  <li>Profile information</li>
                  <li>Account access</li>
                </ul>
                <p className="text-red-600 font-semibold mt-3">This action cannot be undone!</p>
              </div>
              
              <div className="space-y-4">
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={deleteConfirmation.password}
                  onChange={(e) => setDeleteConfirmation(prev => ({ ...prev, password: e.target.value }))}
                />
                <Input
                  placeholder="Type 'DELETE' to confirm"
                  value={deleteConfirmation.confirmation}
                  onChange={(e) => setDeleteConfirmation(prev => ({ ...prev, confirmation: e.target.value }))}
                />
              </div>

              <div className="flex gap-2 mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteProfile(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteProfile}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={deleteConfirmation.confirmation.toUpperCase() !== 'DELETE'}
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RestaurantDashboard;